import { GetLocationVarFromVariables, filterClasses, warn } from "@swivl-mono/helpers";
import { LocationVarKey } from "@swivl-mono/types";
import { type HTMLProps, type ReactNode, useMemo } from "react";

import { facilityVarKeysFor } from "../../Helpers/FeatureFlags/facilityVarKeys";
import { useResolvedScheduleText } from "../../Hooks/useResolvedScheduleText";
import LocationsModel from "../../Models/Locations/Locations.Model";
import LocationVarsModel from "../../Models/Variables/LocationVars.Model";
import "./DataCard.scss";

enum Outlet {
  WRAPPER = "wrapper",
  CONTENT = "content",
  HEADER = "header",
  FOOTER = "footer"
}

interface UserCardProps extends Omit<HTMLProps<HTMLDivElement>, "size" | "className"> {
  classNames?: { [Key in Outlet]?: string };
  footer?: ReactNode;
  locationId?: number;
  pills?: ReactNode;
  size?: "compact" | "full";
  title?: string;
  isRounded?: boolean;
  variant?: "primary" | "secondary";
}

export function DataCardLocation({
  classNames = {},
  footer,
  isRounded = true,
  locationId,
  pills,
  size = "compact",
  title,
  variant = "primary",
  ...rest
}: UserCardProps) {
  const { orgVariables, groupVariables, locationVariables, isLoaded } = LocationVarsModel.useState((s) => s);
  const { locations } = LocationsModel.useState((s) => s);

  // Hours come from the structured Schedule model (ENG-2723 Cortex-only
  // sweep) — the legacy freeform OfficeHours/AccessHours vars are not
  // editable in v3 PM. Only fetched when the full-size card renders them.
  const officeHoursSchedule = useResolvedScheduleText(size === "full" ? locationId : undefined, "office-hours");
  const accessHoursSchedule = useResolvedScheduleText(size === "full" ? locationId : undefined, "gate-access");

  const variants = {
    primary: "bg-grey-50",
    secondary: "bg-white"
  };

  const internalClassNames = {
    wrapper: filterClasses(
      "px-4 py-3 transition-colors duration-200 ease-in-out",
      variants[variant],
      isRounded && "rounded-md",
      classNames.wrapper || ""
    ),
    content: filterClasses("DataCardLocationContent", classNames.content || ""),
    header: filterClasses("DataCardLocationHeader", classNames.header || ""),
    footer: filterClasses("DataCardLocationFooter", classNames.footer || "")
  };

  const locationVars = useMemo(() => {
    if (!locationId) {
      // warn('Location not set')
      return;
    }
    if (!locations) {
      // warn('locations not loaded');
      return;
    }
    const location = locations.find((l) => l.id === locationId);

    if (!location) {
      warn("Locations not found");
      return;
    }

    // ENG-3198: every surface rendering this card is LEGACY (legacy Contact
    // Center conversation details, Escalation History) — so it reads the
    // legacy keys, period. CC v3 has its own FacilityCard on Cortex keys.
    const facilityKeys = facilityVarKeysFor("legacy");

    const name = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      facilityKeys.name,
      location.groupId,
      location.id
    );
    const address = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      facilityKeys.address,
      location.groupId,
      location.id
    );
    const email = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      facilityKeys.email,
      location.groupId,
      location.id
    );
    const phone = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      facilityKeys.phone,
      location.groupId,
      location.id
    );
    const avatarUrl = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      facilityKeys.imageURL,
      location.groupId,
      location.id
    );

    return {
      name: name?.value,
      address: address?.value,
      email: email?.value,
      phone: phone?.value,
      avatarUrl: avatarUrl?.value
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, locations]);

  if (locationId === undefined) {
    // warn('User location not set')
    return;
  }

  return (
    <div className={internalClassNames.wrapper} {...rest}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div className="flex items-center space-x-2 pr-3 text-left">
            {title && <h3 className="font-sans text-lg font-semibold text-black">{locationVars?.name}</h3>}
            <div className="flex gap-2">{pills}</div>
          </div>
          <div className="text-left text-sm text-black">
            <p>{locationVars?.address}</p>
            <p>{locationVars?.email}</p>
            <p>{locationVars?.phone}</p>
          </div>
        </div>
        {locationVars?.avatarUrl && (
          <img className="size-20 rounded-xs object-cover" src={locationVars?.avatarUrl} alt={locationVars?.name + " logo"} />
        )}
      </div>
      {size === "full" && (
        <div className="mt-2.5 flex space-x-1.5 text-sm">
          <div className="flex w-1/2 flex-col">
            <p className="font-semibold">Office Hours</p>
            <p className="whitespace-pre-line">{officeHoursSchedule.text ?? ""}</p>
          </div>
          <div className="flex w-1/2 flex-col">
            <p className="font-semibold">Access Hours</p>
            <p className="whitespace-pre-wrap">{accessHoursSchedule.text ?? ""}</p>
          </div>
        </div>
      )}
      {footer && <div className="mt-2.5 mb-1 text-sm">{footer}</div>}
    </div>
  );
}
